import { useEffect, useState, type ComponentType, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  BadgeCheck,
  Bot,
  Check,
  CheckCircle2,
  ClipboardList,
  Code2,
  Copy,
  ExternalLink,
  Eye,
  Globe,
  KeyRound,
  Link2,
  Lock,
  Maximize2,
  MessageCircle,
  MousePointerClick,
  Palette,
  Plus,
  RefreshCw,
  Route,
  Send,
  ShieldCheck,
  SlidersHorizontal,
  UserRound,
  Users,
  X,
} from 'lucide-react';
import { useTeams } from '@/api/teams';
import { useAiAgents } from '@/api/ai-agents';
import {
  useDeleteWidget,
  useIdentityLookup,
  useSaveWidget,
  useTestIdentityLookup,
  useUpdateIdentityLookup,
  useWidgetSecret,
  useWidgets,
} from '@/api/widgets';
import type { UpdateWidgetInput, Widget } from '@/api/types';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  ErrorState,
  Input,
  PageHeader,
  Segmented,
  Select,
  Skeleton,
  Switch,
  Textarea,
  useToast,
} from '@/components/ui';
import { ApiError } from '@/api/client';
import { cn } from '@/lib/cn';
import { CopyBlock } from '@/components/ui/CopyBlock';
import { FormFieldsEditor } from './FormFieldsEditor';

/** Where widget.js / widget.html are served: this support app's own origin. */
const APP_ORIGIN = window.location.origin;

const BRAND_PRESETS = [
  '#8B5CF6',
  '#6366F1',
  '#0EA5E9',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#EC4899',
  '#0F172A',
];

export function WidgetsPage() {
  const widgets = useWidgets();
  const save = useSaveWidget();
  const toast = useToast();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const list = widgets.data ?? [];
  const selected = list.find((w) => w.id === selectedId) ?? list[0] ?? null;

  const create = () =>
    save.mutate(
      { input: { name: list.length ? `Widget ${list.length + 1}` : 'kollabr.in website' } },
      {
        onSuccess: (w) => (setSelectedId(w.id), toast.success('Widget created')),
        onError: (e) => toast.error(e.message),
      },
    );

  return (
    <>
      <PageHeader
        title="Chat widget"
        description="Add a live-chat bubble to your website or share a direct chat link. Every conversation lands in Live chats, where your AI agent and team pick it up."
        actions={
          <Button onClick={create} loading={save.isPending && !selectedId}>
            <Plus className="h-4 w-4" /> New widget
          </Button>
        }
      />

      {widgets.isPending ? (
        <Skeleton className="h-[560px] rounded-2xl" />
      ) : widgets.error ? (
        <Card>
          <ErrorState error={widgets.error} onRetry={() => void widgets.refetch()} />
        </Card>
      ) : !selected ? (
        <Card>
          <EmptyState
            icon={<MessageCircle className="h-6 w-6" />}
            title="No chat widget yet"
            description="Create one to get an embed code and a shareable chat link."
            action={<Button onClick={create}>Create widget</Button>}
          />
        </Card>
      ) : (
        <>
          {list.length > 1 && (
            <div role="tablist" aria-label="Widgets" className="mb-4 flex flex-wrap gap-2">
              {list.map((w) => (
                <button
                  key={w.id}
                  type="button"
                  role="tab"
                  aria-selected={w.id === selected.id}
                  onClick={() => setSelectedId(w.id)}
                  className={cn(
                    'flex items-center gap-2 rounded-xl border px-3 py-1.5 text-sm font-medium transition-all',
                    w.id === selected.id
                      ? 'border-brand-300 text-brand-700 shadow-card bg-white'
                      : 'border-slate-200/70 bg-white/50 text-slate-600 hover:bg-white',
                  )}
                >
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ background: w.brandColor }}
                    aria-hidden
                  />
                  {w.name}
                  <span
                    className={cn('h-1.5 w-1.5 rounded-full', w.isActive ? 'bg-emerald-500' : 'bg-slate-300')}
                    title={w.isActive ? 'Live' : 'Off'}
                  />
                </button>
              ))}
            </div>
          )}
          <WidgetEditor key={selected.id} widget={selected} onDeleted={() => setSelectedId(null)} />
        </>
      )}
    </>
  );
}

type Tab = 'install' | 'appearance' | 'behaviour' | 'form' | 'users';
type PreviewMode = 'form' | 'report' | 'chat';

const TABS: { value: Tab; label: string; icon: ComponentType<{ className?: string }> }[] = [
  { value: 'install', label: 'Install', icon: Code2 },
  { value: 'appearance', label: 'Appearance', icon: Palette },
  { value: 'behaviour', label: 'Behaviour', icon: SlidersHorizontal },
  { value: 'form', label: 'Issue form', icon: ClipboardList },
  { value: 'users', label: 'Signed-in users', icon: ShieldCheck },
];

function WidgetEditor({ widget, onDeleted }: { widget: Widget; onDeleted: () => void }) {
  const toast = useToast();
  const save = useSaveWidget();
  const remove = useDeleteWidget();
  const teams = useTeams();
  const agents = useAiAgents();
  const [tab, setTab] = useState<Tab>('install');
  const [draft, setDraft] = useState(widget);
  const [domains, setDomains] = useState(widget.allowedDomains.join(', '));
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [preview, setPreview] = useState<PreviewMode>('form');
  useEffect(() => setDraft(widget), [widget]);

  const openTab = (t: Tab) => {
    setTab(t);
    if (t === 'form') setPreview('report');
  };

  const set = <K extends keyof Widget>(k: K, v: Widget[K]) => setDraft((d) => ({ ...d, [k]: v }));
  const parsedDomains = domains
    .split(/[\s,]+/)
    .map((d) => d.trim())
    .filter(Boolean);
  const dirty =
    JSON.stringify({ ...draft, allowedDomains: parsedDomains }) !==
    JSON.stringify({ ...widget, allowedDomains: widget.allowedDomains });

  const agentName = (agents.data ?? []).find((a) => a.id === draft.aiAgentId)?.name ?? null;
  const teamName = (teams.data ?? []).find((t) => t.id === draft.teamId)?.name ?? null;

  const persist = () => {
    const input: UpdateWidgetInput = {
      name: draft.name,
      isActive: draft.isActive,
      brandColor: draft.brandColor,
      position: draft.position,
      title: draft.title,
      subtitle: draft.subtitle,
      welcomeMessage: draft.welcomeMessage,
      allowedDomains: parsedDomains,
      teamId: draft.teamId,
      aiAgentId: draft.aiAgentId,
      askName: draft.askName,
      requireEmail: draft.requireEmail,
      showKnowledgeBase: draft.showKnowledgeBase,
      allowAnonymous: draft.allowAnonymous,
      formCategories: draft.formCategories.map((c) => c.trim()).filter((c) => c.length >= 2),
      formFields: draft.formFields.map((f) => ({
        ...f,
        label: f.label.trim(),
        options: f.type === 'select' ? f.options : [],
      })),
    };
    save.mutate(
      { id: widget.id, input },
      {
        onSuccess: (w) => {
          setDomains(w.allowedDomains.join(', '));
          toast.success('Widget saved');
        },
        onError: (e) => {
          const fields = e instanceof ApiError ? e.fieldErrors : {};
          toast.error(Object.values(fields)[0] ?? e.message);
        },
      },
    );
  };

  const discard = () => {
    setDraft(widget);
    setDomains(widget.allowedDomains.join(', '));
  };

  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px] 2xl:grid-cols-[minmax(0,1fr)_380px]">
      <Card className="min-w-0 overflow-hidden">
        {/* Identity + status */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3 px-5 pt-5 pb-4">
          <span
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white shadow-md"
            style={{ background: `linear-gradient(135deg, ${draft.brandColor}, ${draft.brandColor}cc)` }}
          >
            <MessageCircle className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate text-base font-bold text-slate-900">
                {draft.name || 'Untitled widget'}
              </h2>
              <Badge tone={draft.isActive ? 'green' : 'neutral'} dot>
                {draft.isActive ? 'Live' : 'Off'}
              </Badge>
            </div>
            <KeyChip value={widget.key} />
          </div>
          <div className="flex items-center gap-3">
            <Switch
              checked={draft.isActive}
              onChange={(v) => set('isActive', v)}
              label={draft.isActive ? 'Accepting chats' : 'Switched off'}
              size="sm"
            />
            <a
              href={`${APP_ORIGIN}/widget.html?key=${widget.key}`}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 shadow-xs hover:bg-slate-50"
            >
              Open chat <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>

        {/* Tabs */}
        <div
          role="tablist"
          aria-label="Widget settings"
          className="flex gap-1 overflow-x-auto border-b border-slate-200/80 px-3"
        >
          {TABS.map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={tab === value}
              onClick={() => openTab(value)}
              className={cn(
                '-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm font-medium whitespace-nowrap transition-colors',
                tab === value
                  ? 'border-brand-600 text-brand-700'
                  : 'border-transparent text-slate-500 hover:text-slate-800',
              )}
            >
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>

        <div role="tabpanel" className="p-5 sm:p-6">
          {tab === 'install' && (
            <InstallPanel widget={draft} agentName={agentName} teamName={teamName} onTab={openTab} />
          )}

          {tab === 'appearance' && (
            <div>
              <TabIntro
                title="Appearance"
                text="How the chat looks on your website. Changes show in the preview right away and go live when you save."
              />
              <SettingRow
                title="Widget name"
                text="Internal only — helps your team tell widgets apart. Visitors never see it."
              >
                <Input
                  aria-label="Widget name"
                  value={draft.name}
                  onChange={(e) => set('name', e.target.value)}
                />
              </SettingRow>
              <SettingRow
                title="Brand colour"
                text="Used for the bubble, header, buttons and the visitor’s messages."
              >
                <div className="space-y-2.5">
                  <div className="flex flex-wrap gap-2">
                    {BRAND_PRESETS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        aria-label={`Use ${c}`}
                        onClick={() => set('brandColor', c)}
                        className={cn(
                          'flex h-7 w-7 items-center justify-center rounded-full ring-offset-2 transition-transform hover:scale-110',
                          draft.brandColor.toLowerCase() === c.toLowerCase() && 'ring-2 ring-slate-400',
                        )}
                        style={{ background: c }}
                      >
                        {draft.brandColor.toLowerCase() === c.toLowerCase() && (
                          <Check className="h-3.5 w-3.5 text-white" />
                        )}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      aria-label="Custom colour"
                      value={draft.brandColor}
                      onChange={(e) => set('brandColor', e.target.value)}
                      className="h-10 w-12 cursor-pointer rounded-xl border border-slate-200 bg-white p-1"
                    />
                    <Input
                      aria-label="Brand colour hex"
                      value={draft.brandColor}
                      onChange={(e) => set('brandColor', e.target.value)}
                      className="font-mono"
                      wrapperClassName="flex-1"
                    />
                  </div>
                </div>
              </SettingRow>
              <SettingRow
                title="Header"
                text="The title and the line under it at the top of the chat window."
              >
                <div className="space-y-3">
                  <Input
                    aria-label="Header title"
                    placeholder="Acme Support"
                    value={draft.title}
                    onChange={(e) => set('title', e.target.value)}
                  />
                  <Input
                    aria-label="Header subtitle"
                    placeholder="We usually reply in a few minutes"
                    value={draft.subtitle}
                    onChange={(e) => set('subtitle', e.target.value)}
                  />
                </div>
              </SettingRow>
              <SettingRow
                title="Welcome message"
                text={
                  <>
                    The first message visitors see. For signed-in users you can personalise it with{' '}
                    <code className="rounded bg-slate-100 px-1 font-mono text-[11px]">
                      {'{{first_name | there}}'}
                    </code>
                    , <code className="rounded bg-slate-100 px-1 font-mono text-[11px]">{'{{plan}}'}</code> or
                    any{' '}
                    <code className="rounded bg-slate-100 px-1 font-mono text-[11px]">
                      {'{{attributes.…}}'}
                    </code>
                    .
                  </>
                }
              >
                <Textarea
                  aria-label="Welcome message"
                  rows={3}
                  value={draft.welcomeMessage}
                  onChange={(e) => set('welcomeMessage', e.target.value)}
                />
              </SettingRow>
              <SettingRow
                title="Bubble position"
                text="Which bottom corner of the page the chat bubble sits in."
                last
              >
                <div className="grid grid-cols-2 gap-3">
                  {(['left', 'right'] as const).map((pos) => (
                    <button
                      key={pos}
                      type="button"
                      aria-pressed={draft.position === pos}
                      onClick={() => set('position', pos)}
                      className={cn(
                        'rounded-xl border p-2.5 text-left transition-all',
                        draft.position === pos
                          ? 'border-brand-400 ring-brand-500/15 ring-4'
                          : 'border-slate-200 hover:border-slate-300',
                      )}
                    >
                      <span className="relative block h-14 rounded-lg bg-slate-100">
                        <span
                          className={cn(
                            'absolute bottom-1.5 h-4 w-4 rounded-full shadow',
                            pos === 'left' ? 'left-1.5' : 'right-1.5',
                          )}
                          style={{ background: draft.brandColor }}
                        />
                      </span>
                      <span className="mt-2 block text-xs font-medium text-slate-700">Bottom {pos}</span>
                    </button>
                  ))}
                </div>
              </SettingRow>
            </div>
          )}

          {tab === 'behaviour' && (
            <div>
              <TabIntro
                title="Behaviour"
                text="Who answers new chats, what the visitor fills in before chatting, and where the widget is allowed to appear."
              />
              <SettingRow
                title="AI agent"
                text={
                  <>
                    Answers new chats instantly, day and night. A Workflow engine rule can send some chats to
                    another agent or a team instead.{' '}
                    <Link to="/ai-agents" className="text-brand-600 font-medium hover:underline">
                      Manage AI agents
                    </Link>
                  </>
                }
              >
                <Select
                  aria-label="AI agent"
                  value={draft.aiAgentId ?? ''}
                  onChange={(e) => set('aiAgentId', e.target.value || null)}
                >
                  <option value="">No auto-replies — humans answer</option>
                  {(agents.data ?? [])
                    .filter((a) => a.isEnabled)
                    .map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                </Select>
              </SettingRow>
              <SettingRow
                title="Team queue"
                text={
                  <>
                    The team whose queue receives these chats when a person needs to take over.{' '}
                    <Link to="/settings/teams" className="text-brand-600 font-medium hover:underline">
                      Manage teams
                    </Link>
                  </>
                }
              >
                <Select
                  aria-label="Team queue"
                  value={draft.teamId ?? ''}
                  onChange={(e) => set('teamId', e.target.value || null)}
                >
                  <option value="">No team — general queue</option>
                  {(teams.data ?? [])
                    .filter((t) => t.isActive)
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                </Select>
              </SettingRow>
              <SettingRow
                title="Before the chat starts"
                text="What unknown visitors fill in. Signed-in users skip this form."
              >
                <div className="divide-y divide-slate-100 rounded-xl border border-slate-200/80">
                  <Switch
                    className="p-3"
                    checked={draft.askName}
                    onChange={(v) => set('askName', v)}
                    label="Ask for their name"
                    description="So agents can greet them personally."
                  />
                  <Switch
                    className="p-3"
                    checked={draft.requireEmail}
                    onChange={(v) => set('requireEmail', v)}
                    label="Require an email"
                    description="Lets your team follow up if the visitor leaves."
                  />
                  <Switch
                    className="p-3"
                    checked={draft.showKnowledgeBase}
                    onChange={(v) => set('showKnowledgeBase', v)}
                    label="Show help articles"
                    description="Visitors can search your knowledge base before asking."
                  />
                </div>
              </SettingRow>
              <SettingRow
                title="Allowed websites"
                text="Only these sites (and their subdomains) can show the widget. Leave empty to allow any site. In production, non-kollabr.in domains must also be added to frame-ancestors in nginx.conf."
                last
              >
                <Input
                  aria-label="Allowed websites"
                  placeholder="kollabr.in, admin.kollabr.in"
                  value={domains}
                  onChange={(e) => setDomains(e.target.value)}
                />
                <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-500">
                  <Globe className="h-3.5 w-3.5" />
                  {parsedDomains.length
                    ? `${parsedDomains.length} site${parsedDomains.length > 1 ? 's' : ''} allowed`
                    : 'Any website can embed this widget'}
                </p>
              </SettingRow>
            </div>
          )}

          {tab === 'form' && (
            <div>
              <TabIntro
                title="Issue form"
                text="Build the “Report an issue” form. Answers are checked on the server and shown to agents on the ticket."
              />
              <SettingRow
                title="Issue categories"
                text="Shown as chips at the top of the form and accepted by the website API. Workflow rules can route by them. Up to 12."
              >
                <ChipsInput
                  values={draft.formCategories}
                  onChange={(v) => set('formCategories', v.slice(0, 12))}
                  placeholder="Add a category and press Enter"
                />
              </SettingRow>
              <SettingRow
                title="Your questions"
                text={
                  <>
                    Asked after the issue title, in this order. Mark a question <em>Signed-in users</em> to
                    ask only people your platform has identified (see the Signed-in users tab) — for example
                    an order or campaign ID — and prefill it from their account. Up to 20.
                  </>
                }
                last
              >
                <FormFieldsEditor fields={draft.formFields} onChange={(v) => set('formFields', v)} />
              </SettingRow>
            </div>
          )}

          {tab === 'users' && (
            <IdentityPanel widget={draft} onAllowAnonymous={(v) => set('allowAnonymous', v)} />
          )}
        </div>

        {/* Save bar */}
        <div
          className={cn(
            'sticky bottom-0 flex flex-wrap items-center gap-2 border-t px-5 py-3 backdrop-blur transition-colors',
            dirty ? 'border-amber-200 bg-amber-50/95' : 'border-slate-100 bg-slate-50/90',
          )}
        >
          {dirty ? (
            <span className="text-sm font-medium text-amber-800">You have unsaved changes</span>
          ) : (
            <Button
              variant={confirmDelete ? 'danger' : 'ghost'}
              size="sm"
              loading={remove.isPending}
              onClick={() =>
                confirmDelete
                  ? remove.mutate(widget.id, {
                      onSuccess: () => (toast.success('Widget deleted'), onDeleted()),
                      onError: (e) => toast.error(e.message),
                    })
                  : setConfirmDelete(true)
              }
            >
              {confirmDelete ? 'Confirm delete — the embed code stops working' : 'Delete widget'}
            </Button>
          )}
          <span className="ml-auto flex items-center gap-2">
            {dirty ? (
              <Button variant="ghost" size="sm" onClick={discard}>
                Discard
              </Button>
            ) : (
              <span className="flex items-center gap-1 text-xs text-slate-400">
                <CheckCircle2 className="h-3.5 w-3.5" /> All changes saved
              </span>
            )}
            <Button size="sm" disabled={!dirty} loading={save.isPending} onClick={persist}>
              Save changes
            </Button>
          </span>
        </div>
      </Card>

      <WidgetPreview
        widget={draft}
        agentName={agentName}
        domain={parsedDomains[0] ?? null}
        mode={preview}
        onMode={setPreview}
      />
    </div>
  );
}

/** Title + one-line explanation at the top of a tab. */
function TabIntro({ title, text }: { title: string; text: ReactNode }) {
  return (
    <div className="mb-2">
      <h3 className="text-[15px] font-bold text-slate-900">{title}</h3>
      <p className="mt-0.5 text-sm text-slate-500">{text}</p>
    </div>
  );
}

/** A setting: what it does on the left, the control on the right (stacked on small screens). */
function SettingRow({
  title,
  text,
  children,
  last = false,
}: {
  title: string;
  text: ReactNode;
  children: ReactNode;
  last?: boolean;
}) {
  return (
    <div
      className={cn(
        'grid gap-3 py-5 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:gap-8',
        !last && 'border-b border-slate-100',
      )}
    >
      <div>
        <p className="text-sm font-semibold text-slate-800">{title}</p>
        <p className="mt-1 text-xs leading-relaxed text-slate-500">{text}</p>
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/** The widget key, with one-click copy. */
function KeyChip({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      title="Copy widget key"
      onClick={() =>
        void navigator.clipboard?.writeText(value).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        })
      }
      className="mt-0.5 inline-flex items-center gap-1.5 rounded-md font-mono text-xs text-slate-500 hover:text-slate-800"
    >
      {value}
      {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
    </button>
  );
}

/** Comma-free category editor: Enter adds a chip, × removes it. */
function ChipsInput({
  values,
  onChange,
  placeholder,
}: {
  values: string[];
  onChange: (v: string[]) => void;
  placeholder: string;
}) {
  const [text, setText] = useState('');
  const clean = values.map((v) => v.trim()).filter(Boolean);
  const add = () => {
    const v = text.trim();
    if (v.length >= 2 && !clean.some((c) => c.toLowerCase() === v.toLowerCase())) onChange([...clean, v]);
    setText('');
  };
  return (
    <div className="focus-within:border-brand-300 focus-within:ring-brand-500/10 flex flex-wrap items-center gap-1.5 rounded-xl border border-slate-200 bg-white p-2 focus-within:ring-4">
      {clean.map((c) => (
        <span
          key={c}
          className="bg-brand-50 text-brand-700 ring-brand-100 inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium ring-1"
        >
          {c}
          <button
            type="button"
            aria-label={`Remove ${c}`}
            onClick={() => onChange(clean.filter((x) => x !== c))}
            className="hover:text-brand-900"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <input
        aria-label="Add a category"
        value={text}
        placeholder={clean.length >= 12 ? 'Limit reached' : placeholder}
        disabled={clean.length >= 12}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            add();
          } else if (e.key === 'Backspace' && !text && clean.length) {
            onChange(clean.slice(0, -1));
          }
        }}
        onBlur={add}
        className="min-w-[10rem] flex-1 bg-transparent px-1 py-1 text-sm outline-none placeholder:text-slate-400"
      />
    </div>
  );
}

function InstallPanel({
  widget,
  agentName,
  teamName,
  onTab,
}: {
  widget: Widget;
  agentName: string | null;
  teamName: string | null;
  onTab: (t: Tab) => void;
}) {
  const snippet = `<script src="${APP_ORIGIN}/widget.js"\n  data-widget="${widget.key}"\n  data-color="${widget.brandColor}"\n  data-position="${widget.position}"\n  async></script>`;
  const link = `${APP_ORIGIN}/widget.html?key=${widget.key}`;
  const jsApi = `<button onclick="KollabrSupport.open()">Chat with support</button>`;
  const fullscreenSnippet = `<script src="${APP_ORIGIN}/widget.js"
  data-widget="${widget.key}"
  data-mode="fullscreen"
  async></script>

<script>
  // Your own "Help" button — e.g. on an order page, with that order attached:
  function openHelp(orderId) {
    KollabrSupport.open({
      screen: 'report',            // 'home' | 'messages' | 'help' | 'chat' | 'report'
      category: '${widget.formCategories[0] ?? 'Other'}',
      subject: 'Problem with order ' + orderId,
      context: { orderId },        // shown to agents on the ticket
    });
  }
  // The ← on the help center asks to close; it hides by itself. React to it if you need to:
  KollabrSupport.on('close', () => { /* e.g. router.back() */ });
  KollabrSupport.on('ticket_created', ({ id }) => { /* e.g. analytics */ });
</script>`;
  const inlineSnippet = `<!-- A /help page that IS the help center -->
<div id="support" style="height: 100vh"></div>
<script src="${APP_ORIGIN}/widget.js"
  data-widget="${widget.key}"
  data-mode="fullscreen"
  data-container="#support"
  async></script>`;
  const appLink = `${APP_ORIGIN}/widget.html?key=${widget.key}&mode=fullscreen&screen=report&ctx_orderId=KOL-123&uid={userId}&uh={userHash}`;

  const glance: {
    icon: ComponentType<{ className?: string }>;
    label: string;
    value: string;
    ok: boolean;
    tab?: Tab;
  }[] = [
    {
      icon: CheckCircle2,
      label: 'Status',
      value: widget.isActive ? 'Live — accepting chats' : 'Switched off',
      ok: widget.isActive,
    },
    {
      icon: Bot,
      label: 'AI auto-reply',
      value: agentName ?? 'Off — humans answer',
      ok: !!agentName,
      tab: 'behaviour',
    },
    { icon: Route, label: 'Team queue', value: teamName ?? 'General queue', ok: true, tab: 'behaviour' },
    {
      icon: Globe,
      label: 'Allowed websites',
      value: widget.allowedDomains.length ? widget.allowedDomains.join(', ') : 'Any website',
      ok: true,
      tab: 'behaviour',
    },
  ];

  return (
    <div className="space-y-7">
      <div>
        <TabIntro title="Install" text="Three ways to open the chat. Most sites only need step 1." />
        {!widget.isActive && (
          <p className="mt-3 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800 ring-1 ring-amber-200">
            This widget is switched off — the bubble and chat link show “not available” until you switch it on
            and save.
          </p>
        )}
        <dl className="mt-4 grid gap-2.5 sm:grid-cols-2">
          {glance.map((g) => (
            <div
              key={g.label}
              className="flex items-start gap-3 rounded-xl border border-slate-200/80 bg-slate-50/60 p-3"
            >
              <g.icon
                className={cn('mt-0.5 h-4 w-4 shrink-0', g.ok ? 'text-emerald-600' : 'text-amber-600')}
              />
              <div className="min-w-0 flex-1">
                <dt className="text-[11px] font-semibold tracking-wide text-slate-500 uppercase">
                  {g.label}
                </dt>
                <dd className="truncate text-sm font-medium text-slate-800" title={g.value}>
                  {g.value}
                </dd>
              </div>
              {g.tab && (
                <button
                  type="button"
                  onClick={() => onTab(g.tab!)}
                  className="text-brand-600 shrink-0 text-xs font-medium hover:underline"
                >
                  Change
                </button>
              )}
            </div>
          ))}
        </dl>
      </div>

      <Step
        n={1}
        icon={Code2}
        title="Add the chat bubble to your website"
        text="Paste before </body> on every page that should show the bubble."
      >
        <CopyBlock lang="HTML" value={snippet} />
      </Step>
      <Step
        n={2}
        icon={Link2}
        title="Or share a direct chat link"
        text="A full-page chat for emails, Instagram bio, WhatsApp auto-replies or a QR code. No code needed."
      >
        <CopyBlock lang="URL" value={link} />
        <a
          href={link}
          target="_blank"
          rel="noreferrer noopener"
          className="text-brand-600 mt-2 inline-flex items-center gap-1 text-sm font-medium hover:underline"
        >
          Open the chat page <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </Step>
      <Step
        n={3}
        icon={MousePointerClick}
        title="Open it from your own button (optional)"
        text="Works on any page with the script from step 1. Also available: KollabrSupport.close() and KollabrSupport.toggle()."
      >
        <CopyBlock lang="HTML" value={jsApi} />
      </Step>
      <Step
        n={4}
        icon={Maximize2}
        title="Or a full-screen help center — no bubble"
        text={
          <>
            In-app support like Zepto or Rapido: no floating button and no ✕. Your app opens it from its own
            “Help” button, straight onto a screen with the order or campaign attached, and the back arrow
            hands control back to you. Use it <em>instead of</em> step 1.
          </>
        }
        last
      >
        <div className="space-y-3">
          <CopyBlock lang="HTML" value={fullscreenSnippet} />
          <p className="text-xs text-slate-500">
            Inside a page of your own (the help center fills that element):
          </p>
          <CopyBlock lang="HTML" value={inlineSnippet} />
          <p className="text-xs text-slate-500">
            In the mobile app, open this link in a WebView. <code>ctx_…</code> values reach agents,{' '}
            <code>f_…</code> fill issue-form answers, and the back arrow posts{' '}
            <code>{'{"type":"kollabr:close"}'}</code> to the app.
          </p>
          <CopyBlock lang="URL" value={appLink} />
        </div>
      </Step>

      <div className="bg-brand-50/60 ring-brand-100 flex items-start gap-3 rounded-xl p-4 text-sm text-slate-700 ring-1">
        <BadgeCheck className="text-brand-600 mt-0.5 h-5 w-5 shrink-0" />
        <div>
          <p className="font-semibold text-slate-900">Recognise your signed-in users</p>
          <p className="mt-0.5 text-slate-600">
            Pass their user id so they skip the form, see their past chats, and agents get their full profile.{' '}
            <button
              type="button"
              onClick={() => onTab('users')}
              className="text-brand-600 font-medium hover:underline"
            >
              Set up signed-in users
            </button>
            . Prefer your own “Report an issue” form? See Settings → API &amp; MCP.
          </p>
        </div>
      </div>
    </div>
  );
}

/** A numbered setup step with a connecting line to the next one. */
function Step({
  n,
  icon: Icon,
  title,
  text,
  children,
  last = false,
}: {
  n: number;
  icon: ComponentType<{ className?: string }>;
  title: string;
  text: ReactNode;
  children: ReactNode;
  last?: boolean;
}) {
  return (
    <div className="relative flex gap-4">
      <div className="flex flex-col items-center">
        <span className="bg-brand-600 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white shadow-sm">
          {n}
        </span>
        {!last && <span className="mt-1 w-px flex-1 bg-slate-200" aria-hidden />}
      </div>
      <div className="min-w-0 flex-1 pb-1">
        <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
          <Icon className="text-brand-500 h-4 w-4" /> {title}
        </p>
        <p className="mt-0.5 mb-3 text-xs text-slate-500">{text}</p>
        {children}
      </div>
    </div>
  );
}

/** Live mock of the widget inside a browser frame: the pre-chat form, or a sample conversation. */
function WidgetPreview({
  widget,
  agentName,
  domain,
  mode,
  onMode,
}: {
  widget: Widget;
  agentName: string | null;
  domain: string | null;
  mode: PreviewMode;
  onMode: (mode: PreviewMode) => void;
}) {
  const c = widget.brandColor;
  const side = widget.position === 'left' ? 'left-4' : 'right-4';
  return (
    <div className="xl:sticky xl:top-24 xl:self-start">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-xs font-semibold tracking-wider text-slate-500 uppercase">
          <Eye className="h-3.5 w-3.5" /> Live preview
        </p>
        <Segmented
          label="Preview"
          size="sm"
          value={mode}
          onChange={onMode}
          options={[
            { value: 'form', label: 'Chat' },
            { value: 'report', label: 'Issue' },
            { value: 'chat', label: 'Conversation' },
          ]}
        />
      </div>
      <div className="shadow-card overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
        <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-3 py-2">
          <span className="flex gap-1.5" aria-hidden>
            <span className="h-2.5 w-2.5 rounded-full bg-red-300" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-300" />
          </span>
          <span className="flex-1 truncate rounded-md bg-white px-2 py-0.5 text-center text-[11px] text-slate-400 ring-1 ring-slate-200">
            {domain ?? 'your-website.com'}
          </span>
        </div>
        <div className="dot-canvas relative h-[540px] overflow-hidden">
          {/* page skeleton behind the widget */}
          <div className="space-y-2 p-4 opacity-60" aria-hidden>
            <div className="h-3 w-1/3 rounded bg-slate-200" />
            <div className="h-2 w-2/3 rounded bg-slate-200" />
            <div className="h-2 w-1/2 rounded bg-slate-200" />
          </div>
          {!widget.isActive && (
            <span className="absolute top-14 left-1/2 -translate-x-1/2 rounded-full bg-slate-900/80 px-3 py-1 text-[11px] font-medium text-white">
              Switched off — visitors see “not available”
            </span>
          )}
          <div
            className={cn(
              'absolute bottom-20 w-[288px] overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5',
              side,
            )}
          >
            <div
              className="px-4 py-3.5 text-white"
              style={{ background: `linear-gradient(135deg, ${c}, ${c}cc)` }}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-heading truncate text-[15px] font-bold">{widget.title || 'Support'}</p>
                  <p className="truncate text-xs text-white/85">{widget.subtitle}</p>
                </div>
                <X className="h-4 w-4 shrink-0 text-white/80" aria-hidden />
              </div>
            </div>
            {mode === 'report' ? (
              <ReportPreview widget={widget} />
            ) : mode === 'form' ? (
              <div className="space-y-3 bg-slate-50 p-3">
                {widget.welcomeMessage && (
                  <div className="max-w-[88%] rounded-2xl rounded-tl-md bg-white px-3 py-2 text-[12.5px] text-slate-700 shadow-sm ring-1 ring-slate-100">
                    {widget.welcomeMessage}
                  </div>
                )}
                <div className="space-y-2 rounded-xl bg-white p-3 ring-1 ring-slate-100">
                  {widget.askName && <FakeField>Your name</FakeField>}
                  <FakeField>Email{widget.requireEmail ? '' : ' (optional)'}</FakeField>
                  <div className="h-12 rounded-lg border border-slate-200 px-2.5 pt-1.5 text-[11.5px] text-slate-400">
                    How can we help?
                  </div>
                  <div
                    className="flex h-8 items-center justify-center gap-1.5 rounded-lg text-[12px] font-semibold text-white"
                    style={{ background: c }}
                  >
                    <Send className="h-3 w-3" /> Start chat
                  </div>
                </div>
                {widget.showKnowledgeBase && (
                  <p className="text-center text-[11px] text-slate-400">or search help articles</p>
                )}
              </div>
            ) : (
              <div className="space-y-2.5 bg-slate-50 p-3 text-[12.5px]">
                <div
                  className="ml-auto max-w-[80%] rounded-2xl rounded-tr-md px-3 py-2 text-white"
                  style={{ background: c }}
                >
                  Hi, my payout hasn’t arrived yet
                </div>
                <div className="flex items-end gap-1.5">
                  <span
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white"
                    style={{ background: c }}
                  >
                    {(agentName ?? 'S').charAt(0).toUpperCase()}
                  </span>
                  <div>
                    <p className="mb-0.5 text-[10px] text-slate-400">{agentName ?? 'Support'}</p>
                    <div className="max-w-[210px] rounded-2xl rounded-tl-md bg-white px-3 py-2 text-slate-700 shadow-sm ring-1 ring-slate-100">
                      Sorry about that! Let me check your payout status right away.
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 pl-1 text-[11px] text-slate-400">
                  <span className="flex gap-1" aria-hidden>
                    {[0, 150, 300].map((d) => (
                      <span
                        key={d}
                        className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-300"
                        style={{ animationDelay: `${d}ms` }}
                      />
                    ))}
                  </span>
                  {agentName ? `${agentName} is typing…` : 'An agent will reply here shortly'}
                </div>
                <div className="flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-[11.5px] text-slate-400 ring-1 ring-slate-200">
                  Write a message…
                  <span
                    className="ml-auto flex h-6 w-6 items-center justify-center rounded-full text-white"
                    style={{ background: c }}
                  >
                    <Send className="h-3 w-3" />
                  </span>
                </div>
              </div>
            )}
          </div>
          <span
            className={cn(
              'absolute bottom-4 flex h-12 w-12 items-center justify-center rounded-full text-white shadow-lg',
              side,
            )}
            style={{ background: c }}
            aria-hidden
          >
            <X className="h-5 w-5" />
          </span>
        </div>
      </div>
      <p className="mt-2 text-center text-[11px] text-slate-400">Preview only — save to publish changes.</p>
    </div>
  );
}

/** The “Report an issue” form with the widget's categories and custom questions. */
function ReportPreview({ widget }: { widget: Widget }) {
  const c = widget.brandColor;
  return (
    <div className="max-h-[400px] space-y-2 overflow-y-auto bg-slate-50 p-3">
      <p className="text-[10px] font-semibold tracking-wide text-slate-400 uppercase">What’s it about?</p>
      <div className="flex flex-wrap gap-1">
        {widget.formCategories.slice(0, 6).map((cat, i) => (
          <span
            key={cat}
            className={cn(
              'rounded-full border px-2 py-0.5 text-[10.5px] font-medium',
              i === 0 ? 'border-transparent text-white' : 'border-slate-200 bg-white text-slate-600',
            )}
            style={i === 0 ? { background: c } : undefined}
          >
            {cat}
          </span>
        ))}
      </div>
      <FakeField>Short title, e.g. “Payout not received”</FakeField>
      {widget.formFields.map((f) => (
        <div key={f.id}>
          {f.type === 'checkbox' ? (
            <p className="flex items-center gap-1.5 text-[11.5px] text-slate-600">
              <span className="h-3.5 w-3.5 rounded border border-slate-300 bg-white" />
              {f.label}
              {f.required && <span className="text-red-500">*</span>}
            </p>
          ) : (
            <>
              <p className="mb-0.5 flex items-center gap-1 text-[10.5px] font-medium text-slate-500">
                {f.label}
                {f.required && <span className="text-red-500">*</span>}
                {f.audience === 'signed_in' && (
                  <Lock className="h-2.5 w-2.5 text-slate-400" aria-label="Signed-in only" />
                )}
              </p>
              {f.type === 'textarea' ? (
                <div className="h-10 rounded-lg border border-slate-200 bg-white px-2.5 pt-1.5 text-[11.5px] text-slate-400">
                  {f.placeholder}
                </div>
              ) : (
                <FakeField>
                  {f.prefill !== 'none'
                    ? `Their ${f.prefill}`
                    : f.placeholder || (f.type === 'select' ? 'Choose one…' : '')}
                </FakeField>
              )}
            </>
          )}
          {f.helpText && <p className="mt-0.5 text-[10px] text-slate-400">{f.helpText}</p>}
        </div>
      ))}
      <div className="h-12 rounded-lg border border-slate-200 bg-white px-2.5 pt-1.5 text-[11.5px] text-slate-400">
        What happened?
      </div>
      <div
        className="flex h-8 items-center justify-center gap-1.5 rounded-lg text-[12px] font-semibold text-white"
        style={{ background: c }}
      >
        <Send className="h-3 w-3" /> Submit issue
      </div>
    </div>
  );
}

function FakeField({ children }: { children: ReactNode }) {
  return (
    <div className="h-8 truncate rounded-lg border border-slate-200 bg-white px-2.5 text-[11.5px] leading-8 text-slate-400">
      {children}
    </div>
  );
}

function IdentityPanel({
  widget,
  onAllowAnonymous,
}: {
  widget: Widget;
  onAllowAnonymous: (v: boolean) => void;
}) {
  const toast = useToast();
  const secret = useWidgetSecret();
  const [revealed, setRevealed] = useState<string | null>(null);
  const [confirmRotate, setConfirmRotate] = useState(false);

  const reveal = (rotate = false) =>
    secret.mutate(
      { id: widget.id, rotate },
      {
        onSuccess: (s) => {
          setRevealed(s);
          setConfirmRotate(false);
          if (rotate) toast.success('Secret rotated — update it on your backend now');
        },
        onError: (e) => toast.error(e.message),
      },
    );

  const backendCode = `// Your backend (Node.js). Keep the secret server-side — never in browser code.
import crypto from 'node:crypto';

const userHash = crypto
  .createHmac('sha256', process.env.KOLLABR_SUPPORT_WIDGET_SECRET)
  .update(String(user.id))
  .digest('hex');
// Render user.id and userHash into the page (next step).`;

  const pageCode = `<script src="${APP_ORIGIN}/widget.js"
  data-widget="${widget.key}"
  data-user-id="{{ user.id }}"
  data-user-hash="{{ userHash }}"
  async></script>

<!-- Or after login in a single-page app: -->
<script>
  KollabrSupport.identify({ userId: user.id, userHash });
  // On sign-out:
  KollabrSupport.logout();
</script>`;

  const appLink = `${APP_ORIGIN}/widget.html?key=${widget.key}&uid={userId}&uh={userHash}`;

  return (
    <div className="space-y-7">
      <TabIntro
        title="Signed-in users"
        text="Recognise people who are logged in to your platform: they skip the form, see their past chats, and agents see their full profile. Each user id is signed, so nobody can pose as someone else."
      />
      <section>
        <h3 className="mb-1 text-sm font-semibold text-slate-900">Who can chat</h3>
        <p className="mb-3 text-xs text-slate-500">Saved with “Save changes”.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          {(
            [
              {
                value: true,
                icon: <Users className="h-4 w-4" />,
                title: 'Everyone',
                text: 'Unknown visitors fill a short form; your signed-in users are recognised automatically.',
              },
              {
                value: false,
                icon: <UserRound className="h-4 w-4" />,
                title: 'Signed-in users only',
                text: 'For in-app support. Visitors without a verified identity are asked to log in.',
              },
            ] as const
          ).map((o) => (
            <button
              key={String(o.value)}
              type="button"
              aria-pressed={widget.allowAnonymous === o.value}
              onClick={() => onAllowAnonymous(o.value)}
              className={cn(
                'rounded-xl border p-3.5 text-left transition-all',
                widget.allowAnonymous === o.value
                  ? 'border-brand-400 bg-brand-50/50 ring-brand-500/15 ring-4'
                  : 'border-slate-200 hover:border-slate-300',
              )}
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <span className="text-brand-600">{o.icon}</span>
                {o.title}
              </span>
              <span className="mt-1 block text-xs text-slate-500">{o.text}</span>
            </button>
          ))}
        </div>
      </section>

      <Step
        n={1}
        icon={KeyRound}
        title="Copy the identity secret"
        text="Your backend signs each user id with this secret, so nobody can pretend to be another user by changing an id in the browser. Keep it server-side."
      >
        <div className="flex flex-wrap items-center gap-2">
          <code className="bg-ink-950 min-w-0 flex-1 truncate rounded-xl px-4 py-2.5 font-mono text-[12.5px] text-slate-100">
            {revealed ?? 'wsec_••••••••••••••••••••••••••••'}
          </code>
          {revealed ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                void navigator.clipboard?.writeText(revealed).then(() => toast.success('Secret copied'))
              }
            >
              <Copy className="h-3.5 w-3.5" /> Copy
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              loading={secret.isPending && !confirmRotate}
              onClick={() => reveal()}
            >
              <Eye className="h-3.5 w-3.5" /> Reveal
            </Button>
          )}
          <Button
            variant={confirmRotate ? 'danger' : 'ghost'}
            size="sm"
            loading={secret.isPending && confirmRotate}
            onClick={() => (confirmRotate ? reveal(true) : setConfirmRotate(true))}
          >
            <RefreshCw className="h-3.5 w-3.5" />{' '}
            {confirmRotate ? 'Confirm — old signatures stop working' : 'Rotate'}
          </Button>
        </div>
      </Step>

      <Step
        n={2}
        icon={ShieldCheck}
        title="Sign the user id on your backend"
        text="Run this on your server for the logged-in user."
      >
        <CopyBlock lang="Node.js" value={backendCode} />
      </Step>
      <Step
        n={3}
        icon={Code2}
        title="Pass it to the widget"
        text="Replace {{ … }} with your template syntax. Without these, the visitor is treated as unknown."
      >
        <CopyBlock lang="HTML" value={pageCode} />
      </Step>
      <Step
        n={4}
        icon={Link2}
        title="Inside your mobile app (optional)"
        text="Open this URL in a webview with the user’s id and hash filled in — they land straight in their support chat."
        last
      >
        <CopyBlock lang="URL" value={appLink} />
      </Step>

      <LookupSettings />
    </div>
  );
}

function LookupSettings() {
  const toast = useToast();
  const lookup = useIdentityLookup();
  const update = useUpdateIdentityLookup();
  const test = useTestIdentityLookup();
  const [form, setForm] = useState<{
    enabled: boolean;
    urlTemplate: string;
    authHeader: string;
    authValue: string;
  } | null>(null);
  const [testId, setTestId] = useState('');
  const [result, setResult] = useState<string | null>(null);

  useEffect(() => {
    if (lookup.data && !form) {
      setForm({
        enabled: lookup.data.enabled,
        urlTemplate: lookup.data.urlTemplate,
        authHeader: lookup.data.authHeader || 'Authorization',
        authValue: '',
      });
    }
  }, [lookup.data, form]);

  if (lookup.isPending || !form) return <Skeleton className="h-64 rounded-2xl" />;
  if (lookup.error) return <ErrorState error={lookup.error} onRetry={() => void lookup.refetch()} />;

  const save = () =>
    update.mutate(
      {
        enabled: form.enabled,
        urlTemplate: form.urlTemplate.trim(),
        authHeader: form.authHeader.trim(),
        ...(form.authValue ? { authValue: form.authValue } : {}),
      },
      {
        onSuccess: () => {
          setForm({ ...form, authValue: '' });
          toast.success('User lookup saved');
        },
        onError: (e) => {
          const fields = e instanceof ApiError ? e.fieldErrors : {};
          toast.error(Object.values(fields)[0] ?? e.message);
        },
      },
    );

  const contract = `GET ${form.urlTemplate || 'https://api.example.com/support/users/{userId}'}
${form.authHeader || 'Authorization'}: ••••

200 → {
  "name": "Ananya Iyer",            // required
  "email": "ananya@example.com",
  "phone": "+91 98765 43210",
  "type": "creator",                 // creator | business | brand | other
  "plan": "pro",
  "city": "Bengaluru",
  "avatarUrl": "https://…",
  "attributes": {                    // anything agents should see
    "instagramFollowers": 85400,
    "walletBalance": "₹12,450",
    "kycVerified": true
  }
}
(may also be wrapped as { "data": { … } }; 404 = unknown user)`;

  return (
    <section className="space-y-4 rounded-2xl border border-slate-200/80 p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Fetch user details from your platform</h3>
          <p className="text-xs text-slate-500">
            When a signed-in user opens the chat, we call this endpoint with their id and keep their customer
            profile in sync (refreshed at most every 10 minutes). Shared by all widgets.
          </p>
        </div>
        <Checkbox
          label="Enabled"
          checked={form.enabled}
          onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-[1fr_180px]">
        <Input
          label="Lookup URL"
          placeholder="https://api.example.com/support/users/{userId}"
          hint="Must contain {userId}. Your backend returns the user’s profile as JSON (see the format below)."
          error={
            form.urlTemplate.trim() && !form.urlTemplate.includes('{userId}')
              ? 'Add {userId} where the user id goes, e.g. https://api.example.com/support/users/{userId}'
              : undefined
          }
          className="font-mono text-[13px]"
          value={form.urlTemplate}
          onChange={(e) => setForm({ ...form, urlTemplate: e.target.value })}
        />
        <Input
          label="Auth header"
          value={form.authHeader}
          onChange={(e) => setForm({ ...form, authHeader: e.target.value })}
        />
      </div>
      <Input
        label="Auth header value"
        type="password"
        autoComplete="off"
        placeholder={
          lookup.data.hasAuthValue ? `•••• ${lookup.data.authValueLast4} — leave blank to keep` : 'Bearer …'
        }
        hint="Encrypted on the server. Use a key that can only read user profiles."
        value={form.authValue}
        onChange={(e) => setForm({ ...form, authValue: e.target.value })}
      />
      <div className="flex justify-end">
        <Button size="sm" loading={update.isPending} onClick={save}>
          Save lookup
        </Button>
      </div>

      <div className="border-t border-slate-100 pt-4">
        <p className="mb-2 text-sm font-medium text-slate-800">Test with a real user id</p>
        <div className="flex gap-2">
          <Input
            aria-label="User id"
            placeholder="e.g. 9001"
            value={testId}
            onChange={(e) => setTestId(e.target.value)}
            wrapperClassName="flex-1"
          />
          <Button
            variant="secondary"
            loading={test.isPending}
            disabled={!testId.trim()}
            onClick={() =>
              test.mutate(
                {
                  userId: testId.trim(),
                  ...(form.urlTemplate.trim() ? { urlTemplate: form.urlTemplate.trim() } : {}),
                  ...(form.authHeader.trim() ? { authHeader: form.authHeader.trim() } : {}),
                  ...(form.authValue ? { authValue: form.authValue } : {}),
                },
                {
                  onSuccess: (r) => setResult(JSON.stringify(r, null, 2)),
                  onError: (e) => {
                    setResult(null);
                    toast.error(e.message);
                  },
                },
              )
            }
          >
            Fetch
          </Button>
        </div>
        {result && (
          <pre className="mt-3 max-h-64 overflow-auto rounded-xl bg-emerald-50 p-3 font-mono text-xs text-emerald-900 ring-1 ring-emerald-200">
            {result}
          </pre>
        )}
      </div>

      <details className="group">
        <summary className="cursor-pointer text-sm font-medium text-slate-600 hover:text-slate-900">
          What the endpoint must return
        </summary>
        <pre className="bg-ink-950 mt-2 overflow-x-auto rounded-xl p-4 font-mono text-[12px] leading-relaxed text-slate-100">
          {contract}
        </pre>
      </details>
    </section>
  );
}
